import { useCallback, useEffect, useRef, useState } from "react";
import { useInterval } from "./useInterval";
import { useDocumentVisibility } from "./useDocumentVisibility";

// Default progressive-backoff schedule (ms), ported verbatim from
// fe-react `useProgressivePolling`: 1s, 2s, 3s, 5s, 8s, 10s, then a final
// interval (15s) repeated indefinitely. Fast feedback up front, then it
// settles so a long-running job does not hammer the backend.
const DEFAULT_SCHEDULE_MS = [1000, 2000, 3000, 5000, 8000, 10000] as const;
const DEFAULT_FINAL_INTERVAL_MS = 15000;

/**
 * Options for {@link useProgressivePolling}.
 *
 * The only required fields are `pollFn` + `isActive`; everything else has a
 * sensible default so the common case ("poll until ready, backing off, pausing
 * when the tab is hidden") is just those two plus `shouldStopPolling`.
 */
export interface UseProgressivePollingOptions {
  /**
   * The poll action run on each tick (an RTK Query refetch, a service call, a
   * dispatch, etc.). Captured in a ref by the underlying `useInterval`, so an
   * inline closure is safe and will not re-arm the timer on every render.
   */
  pollFn: () => void;

  /**
   * Master gate. When `false`, the timer is cleared and the schedule resets to
   * the start (re-arming begins again from the first step).
   */
  isActive: boolean;

  /**
   * Terminal predicate, evaluated before each scheduled tick. Returning `true`
   * stops polling and resets the schedule. Consumers pass things like
   * "every file is uploaded or failed" or "the tracked item was removed".
   */
  shouldStopPolling?: () => boolean;

  /**
   * Changing any entry restarts the schedule from the first step. Pass the
   * filters / page / page-size that, when changed, should give the user fast
   * feedback again (e.g. `[filters.status, filters.type]`, `[page, pageSize]`).
   */
  resetDependencies?: readonly unknown[];

  /**
   * Optional overall cap (ms). Once this much wall-clock time has elapsed since
   * polling (re)started, the timer stops and {@link onTimeout} fires once. Use
   * it to bound a job that may never reach a terminal state (e.g. a 60s upload
   * extraction cap). Hidden-tab pauses do not extend the cap, matching the
   * fixed-loop behavior it replaces.
   */
  maxDurationMs?: number;

  /**
   * Called once when `maxDurationMs` elapses before a terminal state is
   * reached. Typically marks the item "still processing, try again".
   */
  onTimeout?: () => void;

  /**
   * Pause polling while the document is hidden (backgrounded tab), resuming
   * from the same point when it becomes visible again. Defaults to `true`,
   * which is the better behavior for almost every consumer. Pass `false` for
   * the rare always-on case.
   */
  pauseWhenHidden?: boolean;

  /**
   * Escape hatch: override the backoff schedule (ms per step). Defaults to
   * `[1000, 2000, 3000, 5000, 8000, 10000]`.
   */
  schedule?: readonly number[];

  /**
   * Escape hatch: the interval (ms) used once the schedule is exhausted,
   * repeated indefinitely. Defaults to `15000`.
   */
  finalIntervalMs?: number;
}

/** Return value of {@link useProgressivePolling}. */
export interface UseProgressivePollingReturn {
  /**
   * Restart the schedule from the first step. Call after an action that should
   * give the user fast feedback again (e.g. a fresh upload).
   */
  resetPollingSchedule: () => void;

  /**
   * The current step index into the schedule (0-based). Exposed for debugging
   * and for the rare consumer that wants to surface "how aggressively are we
   * polling right now".
   */
  currentPollIndex: number;
}

/**
 * Progressive (backoff) polling, visibility-aware. A headless timing engine
 * that drives status morphs in consuming components (an upload chip going
 * indexing then ready, a generation job, a vault re-index); it renders nothing
 * itself.
 *
 * Composed from the library's own `useInterval` (declarative `setInterval`,
 * paused on `delay === null`) and `useDocumentVisibility` (SSR-safe). The
 * backoff state machine on top recomputes the delay from `currentPollIndex`
 * against the schedule and hands it to `useInterval`; the delay is `null`
 * (paused) whenever polling is not active, a terminal/predicate stop fires, the
 * `maxDurationMs` cap elapses, or the tab is hidden and `pauseWhenHidden` is on.
 *
 * Schedule: 1s, 2s, 3s, 5s, 8s, 10s, then 15s indefinitely (override via
 * `schedule` / `finalIntervalMs`).
 *
 * SSR-safe: no `window`/`document` is read at module load (visibility comes
 * from the SSR-safe hook), so it is safe to import on the server.
 *
 * @example
 * ```tsx
 * const { resetPollingSchedule } = useProgressivePolling({
 *   pollFn: () => refetch(),
 *   isActive: isPanelOpen,
 *   shouldStopPolling: () => files.every((f) => f.status === "uploaded" || f.status === "failed"),
 *   resetDependencies: [filters.status, filters.type],
 * });
 *
 * // After a fresh upload, give the user fast feedback again:
 * resetPollingSchedule();
 * ```
 */
export function useProgressivePolling({
  pollFn,
  isActive,
  shouldStopPolling,
  resetDependencies = [],
  maxDurationMs,
  onTimeout,
  pauseWhenHidden = true,
  schedule = DEFAULT_SCHEDULE_MS,
  finalIntervalMs = DEFAULT_FINAL_INTERVAL_MS,
}: UseProgressivePollingOptions): UseProgressivePollingReturn {
  const [pollIndex, setPollIndex] = useState(0);
  const [resetToken, setResetToken] = useState(0);
  const visible = useDocumentVisibility();

  // Keep the latest predicate / callbacks in refs so they don't have to be
  // stable identities at the call site and don't churn the effect deps below.
  const shouldStopRef = useRef(shouldStopPolling);
  shouldStopRef.current = shouldStopPolling;
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  // Restart the schedule from the first step.
  const resetPollingSchedule = useCallback(() => {
    setResetToken((prev) => prev + 1);
  }, []);

  // A bumped reset token zeroes the index, which re-arms at the first step.
  useEffect(() => {
    setPollIndex(0);
  }, [resetToken]);

  // Auto-reset when any tracked dependency changes (filters, page, ...). The
  // spread is intentional: the effect's dep array IS the consumer's list.
  useEffect(() => {
    if (resetDependencies.length > 0) {
      resetPollingSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDependencies);

  // Resolve the terminal-stop predicate once per render so both the delay
  // computation and the cap check below agree within a single tick.
  const stopped = isActive && shouldStopRef.current ? shouldStopRef.current() : false;

  // When `isActive` flips off we mirror the donor by snapping the index back to
  // the start so the next active run begins fast again.
  useEffect(() => {
    if (!isActive) setPollIndex(0);
  }, [isActive]);

  // Wall-clock cap. A single timer armed when the active window opens fires
  // after `maxDurationMs` of real time, flips `capElapsed` to pause the poll,
  // and runs `onTimeout` once. It is deliberately NOT gated on visibility:
  // hidden-tab pauses must not extend the cap (matching the fixed-loop it
  // replaces). The window re-opens on a reset-token bump. No `Date.now()` math,
  // so it behaves under fake timers too.
  const [capElapsed, setCapElapsed] = useState(false);
  useEffect(() => {
    setCapElapsed(false);
    if (!isActive || maxDurationMs == null) return;
    const id = setTimeout(() => {
      setCapElapsed(true);
      if (!shouldStopRef.current?.()) onTimeoutRef.current?.();
    }, maxDurationMs);
    return () => clearTimeout(id);
  }, [isActive, maxDurationMs, resetToken]);

  // Compute the delay for the current step. `null` pauses `useInterval`: not
  // active, terminal stop, cap elapsed, or hidden tab (when opted in).
  const paused =
    !isActive || stopped || capElapsed || (pauseWhenHidden && !visible);
  const stepDelay = pollIndex < schedule.length ? schedule[pollIndex] : finalIntervalMs;
  const delay = paused ? null : stepDelay;

  // One tick at the current delay, then advance the step so the next arm uses
  // the next (longer) interval. Re-checks the terminal predicate at fire time
  // so a job that finishes mid-interval does not run one extra poll.
  useInterval(() => {
    if (shouldStopRef.current?.()) {
      setPollIndex(0);
      return;
    }
    pollFn();
    setPollIndex((prev) => (prev < schedule.length ? prev + 1 : prev));
  }, delay);

  return {
    resetPollingSchedule,
    currentPollIndex: pollIndex,
  };
}
